import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { crefisaService } from "@/app/services/crefisaService";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Simulacao } from "@/app/services/crefisaService/createSimulations";
import { useToast } from "@/view/components/ui/use-toast";

const getOffersModalSchema = z.object({});

type getOffersModalFormData = z.infer<typeof getOffersModalSchema>;

interface UseOffersModalProps {
  onContinue?: () => void;
}

export default function useOffersModal({ onContinue }: UseOffersModalProps) {
  const { simulationData, customerData, setCustomer, setProposal } =
    useCrefisaContext();
  const { toast } = useToast();

  const [selectedInstallment, setSelectedInstallment] =
    useState<Simulacao | null>(null);
  const [manualRegistration, setManualRegistration] = useState("");
  const [manualRegistrationDigit, setManualRegistrationDigit] = useState("");

  const getOffersModalForm = useForm<getOffersModalFormData>({
    resolver: zodResolver(getOffersModalSchema),
    mode: "onChange",
  });

  const { handleSubmit } = getOffersModalForm;

  const {
    data,
    isPending,
    mutate: runSimulation,
  } = useMutation({
    mutationFn: () =>
      crefisaService.createSimulations(simulationData!.clientId!, {
        cpf: customerData!.cpf,
        isRefinancing: simulationData!.isRefinancing ?? false,
        phone: customerData!.phone,
      }),
    onError: (err: {
      statusCode?: number;
      message?: string;
      mensagem?: string;
    }) => {
      toast({
        title: "Erro ao gerar proposta",
        description:
          err?.mensagem ||
          err?.message ||
          "Não foi possível realizar a simulação.",
        variant: "destructive",
      });
    },
  });

  const isFetching = isPending;
  const isLoading = isPending && !data;

  const borrowedValue = selectedInstallment?.valorSolicitado ?? 0;

  const submitModal = handleSubmit(() => {
    if (!selectedInstallment) return;

    setCustomer({
      income: {
        registration: data?.matricula ?? manualRegistration,
        registrationDigit: data?.digitoMatricula ?? manualRegistrationDigit,
        valueIncome: 0,
        codeBenefitSpecie: 0,
      },
    });

    setProposal({
      value: selectedInstallment.valorSolicitado,
      prazo: selectedInstallment.quantidadeParcelas,
      simulationId: selectedInstallment.idSimulacao,
      refinancingId: selectedInstallment.idContratoRefin,
    });
    onContinue?.();
  });

  useEffect(() => {
    runSimulation();
  }, [runSimulation]);

  useEffect(() => {
    if (data?.simulacoes?.length) {
      setSelectedInstallment(data.simulacoes[data.simulacoes.length - 1]);
    }
  }, [data]);

  useEffect(() => {
    if (data?.openFinanceObrigatorio) {
      const consentId = data?.dadosConsentimento?.consentimentoId;

      if (consentId) {
        setCustomer({
          dataConsentId: consentId,
        });
      }
    }
  }, [data, setCustomer]);

  const handleCopyConsentLink = async () => {
    if (!data?.linkConsentimento) return;

    try {
      await navigator.clipboard.writeText(data.linkConsentimento);
      toast({
        title: "Link copiado",
        description:
          "Link do consentimento copiado para a área de transferência.",
      });
    } catch {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o link.",
        variant: "destructive",
      });
    }
  };

  const errorMessage =
    !isFetching && data?.sucesso === false
      ? data?.mensagemLimiteTomado || "Não foi possível realizar a simulação."
      : null;

  return {
    form: getOffersModalForm,
    submitModal,
    isPending: isPending || isLoading || isFetching,
    isFetching,
    installments: data?.simulacoes ?? [],
    selectedInstallment,
    setSelectedInstallment,
    borrowedValue,
    commission: data?.commission,
    matricula: data?.matricula,
    digitoMatricula: data?.digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    linkConsentimento: data?.linkConsentimento,
    handleCopyConsentLink,
    errorMessage,
  };
}
